import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Rotary knob — vertical drag to change value, double-click to reset to default.
 *
 * 2026 visual:
 *  - Outer SVG arc gauge (sky → fuchsia as value rises). Pulsing glow on drag.
 *  - Inner metallic disc: radial gradient + multi-stop linear so the cap reads
 *    as a brushed-aluminum knob lit from above. The tick rotates with `angle`.
 *  - Inset shadow ring stitched around the disc edge for crisp tactile depth.
 *
 * Mouse-down captures pointer events globally so the user can drag past the
 * disc boundary without losing the grip. 1 px = ~0.4% of range; Shift = 10×
 * fine control. Wheel-scroll for fast tweaks (Shift = 0.5× fine).
 *
 * Accessibility: the rendered <output> is mirrored as text. `aria-valuemin`/
 * `aria-valuemax` on the wrapper + `role="slider"` make it screen-reader
 * navigable. Keyboard support stays in the parent (a hidden range input
 * could be added if the team needs full kbd-arrow nudge — currently this
 * knob is mouse/touch only by design, matching DAW conventions).
 */
export default function Knob({
  value, min, max, step = 0.01, defaultValue, unit = "", label,
  onChange, formatValue,
  size = 56, testId,
}) {
  const knobRef = useRef(null);
  const dragRef = useRef({ active: false, startY: 0, startValue: 0, shift: false });
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  const range = max - min;
  const norm = Math.max(0, Math.min(1, (value - min) / range));
  // Knob arc spans -135° (left) → +135° (right), 270° total sweep.
  const angle = -135 + norm * 270;
  const fmt = (v) => (formatValue ? formatValue(v) : v.toFixed(step >= 1 ? 0 : (step >= 0.1 ? 1 : 2))) + (unit ? ` ${unit}` : "");

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { active: true, startY: e.clientY, startValue: value, shift: e.shiftKey };
    setDragging(true);
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
  }, [value]);

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d.active) return;
      const dy = d.startY - e.clientY;
      const speed = (e.shiftKey || d.shift) ? 0.001 : 0.004;
      const next = Math.max(min, Math.min(max, d.startValue + dy * speed * range));
      const snapped = Math.round(next / step) * step;
      onChange(Math.max(min, Math.min(max, snapped)));
    };
    const onUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      setDragging(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [min, max, range, step, onChange]);

  const handleDoubleClick = () => {
    if (typeof defaultValue === "number") onChange(defaultValue);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const fine = e.shiftKey ? 0.5 : 1;
    const delta = -Math.sign(e.deltaY) * step * 4 * fine;
    onChange(Math.max(min, Math.min(max, value + delta)));
  };

  // Active arc is sky → fuchsia as value rises; the dragging state amps the
  // glow ring so producers get tactile feedback while tweaking.
  const arcStroke = hovered || dragging ? "rgb(232,121,249)" : "rgb(125,211,252)";
  const r = size / 2 - 4;
  const arcCircumference = (270 / 360) * 2 * Math.PI * r;

  return (
    <div className="flex flex-col items-center gap-1.5" data-testid={testId}>
      <div
        ref={knobRef}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        onWheel={handleWheel}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title={`${label} · drag to adjust, double-click to reset (default ${defaultValue ?? "—"})`}
        role="slider"
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        className="relative cursor-ns-resize select-none"
        style={{ width: size, height: size }}
      >
        {/* Outer glow ring — only renders during interaction so idle knobs
            stay calm. Soft fuchsia bloom tracks hover & drag intensity. */}
        <div
          className="absolute inset-0 rounded-full transition-opacity duration-200"
          style={{
            opacity: dragging ? 1 : (hovered ? 0.55 : 0),
            boxShadow: "0 0 22px 2px rgba(232,121,249,0.45)",
          }}
        />

        {/* SVG arc gauge */}
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 pointer-events-none">
          {/* Background arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="3"
            strokeDasharray={`${arcCircumference} 1000`}
            transform={`rotate(135 ${size / 2} ${size / 2})`}
          />
          {/* Value arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={arcStroke}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${norm * arcCircumference} 1000`}
            transform={`rotate(135 ${size / 2} ${size / 2})`}
            style={{
              transition: "stroke 150ms ease-out",
              filter: dragging ? "drop-shadow(0 0 4px rgba(232,121,249,0.7))" : undefined,
            }}
          />
        </svg>

        {/* Inner metallic disc — radial + linear gradient build a lit-metal look.
            Box-shadow stack stitches an inset bevel + outer drop for depth. */}
        <div
          className="absolute rounded-full"
          style={{
            inset: 8,
            transform: `rotate(${angle}deg)`,
            transition: dragRef.current.active ? "none" : "transform 90ms cubic-bezier(0.2,0.8,0.2,1)",
            backgroundImage:
              "radial-gradient(circle at 30% 22%, rgba(255,255,255,0.32) 0%, transparent 45%), " +
              "linear-gradient(160deg, #3b3f5a 0%, #1d2034 55%, #0b0d1c 100%)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.24), " +
              "inset 0 -2px 4px rgba(0,0,0,0.55), " +
              "inset 0 0 0 1px rgba(0,0,0,0.4), " +
              "0 4px 12px rgba(0,0,0,0.5)",
          }}
        >
          {/* Tick — embossed indicator. Two stacked spans (light + dark) give
              it a chiseled edge on the metallic disc. */}
          <span
            className="absolute left-1/2 top-1.5 h-[10px] w-[3px] -translate-x-1/2 rounded-full"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(232,121,249,0.85) 100%)",
              boxShadow:
                "0 0 6px rgba(232,121,249,0.7), 0 0 0 0.5px rgba(0,0,0,0.5)",
            }}
          />
        </div>
      </div>

      <div className="flex flex-col items-center leading-tight">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">{label}</span>
        <span
          data-testid={testId ? `${testId}-value` : undefined}
          className="font-mono text-[10px] tabular-nums text-white"
        >
          {fmt(value)}
        </span>
      </div>
    </div>
  );
}
